import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useSession } from "../auth/SessionContext";

type TerminalStatus = "connecting" | "open" | "closed" | "error";

export function TerminalPage() {
  const { chassisId } = useParams<{ chassisId: string }>();
  const { user } = useSession();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<TerminalStatus>("connecting");
  const [reason, setReason] = useState<string | null>(null);
  const [terminalSessionId, setTerminalSessionId] = useState<string | null>(null);
  const [showRecInfo, setShowRecInfo] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !chassisId || !user) return;

    const term = new Terminal({
      fontFamily: "Menlo, Consolas, monospace",
      fontSize: 13,
      cursorBlink: true,
      theme: {
        background: "#020617",
        foreground: "#e2e8f0",
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    fit.fit();
    termRef.current = term;

    const onResize = () => {
      try {
        fit.fit();
      } catch {
        /* noop */
      }
    };
    window.addEventListener("resize", onResize);

    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/api/chassis/${chassisId}/terminal`);
    wsRef.current = ws;

    ws.onopen = () => {
      // wait for the server's "Connected to ..." status message before
      // flipping local state — that's when the SSH session is actually live.
    };

    ws.onmessage = (event) => {
      let msg: {
        type?: string;
        content?: string;
        terminal_session_id?: string;
      };
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      if (msg.type === "status" && typeof msg.content === "string") {
        setStatus("open");
        if (typeof msg.terminal_session_id === "string") {
          setTerminalSessionId(msg.terminal_session_id);
        }
        term.writeln(`\x1b[32m[status] ${msg.content}\x1b[0m`);
      } else if (msg.type === "output" && typeof msg.content === "string") {
        term.write(msg.content);
      } else if (msg.type === "error" && typeof msg.content === "string") {
        setStatus("error");
        setReason(msg.content);
        term.writeln(`\x1b[31m[error] ${msg.content}\x1b[0m`);
      }
    };

    ws.onclose = (event) => {
      setStatus("closed");
      const friendly = event.reason || `WebSocket closed (code ${event.code})`;
      setReason((prev) => prev ?? friendly);
      term.writeln(`\x1b[33m[closed] ${friendly}\x1b[0m`);
    };

    term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "input", content: data }));
      }
    });

    return () => {
      window.removeEventListener("resize", onResize);
      try {
        ws.close();
      } catch {
        /* noop */
      }
      term.dispose();
      termRef.current = null;
      wsRef.current = null;
    };
  }, [chassisId, user]);

  return (
    <section className="flex h-[calc(100vh-7rem)] flex-col gap-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to={`/chassis/${chassisId}`}
            className="text-sm text-slate-400 hover:text-slate-200"
          >
            ← Chassis
          </Link>
          <h1 className="text-xl font-semibold tracking-tight">Terminal</h1>
          <span
            className={`rounded border px-2 py-0.5 text-xs ${
              status === "open"
                ? "border-emerald-700 bg-emerald-900 text-emerald-200"
                : status === "error"
                  ? "border-red-700 bg-red-900 text-red-200"
                  : status === "closed"
                    ? "border-slate-700 bg-slate-800 text-slate-300"
                    : "border-amber-700 bg-amber-900 text-amber-200"
            }`}
          >
            {status}
          </span>
          {terminalSessionId && (
            <button
              type="button"
              onClick={() => setShowRecInfo(true)}
              title="This session is being recorded for review"
              className="flex items-center gap-1 rounded border border-red-700 bg-red-950/40 px-2 py-0.5 text-xs text-red-200 hover:bg-red-950"
            >
              <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
              REC
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {reason && <div className="text-xs text-slate-400">{reason}</div>}
          {terminalSessionId && (
            <a
              href={`/api/terminal-sessions/${terminalSessionId}/transcript.txt`}
              download
              className="rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800"
            >
              Download session
            </a>
          )}
        </div>
      </header>
      {showRecInfo && (
        <div className="rounded-lg border border-red-700 bg-red-950/40 p-3 text-xs text-red-100">
          <p className="font-semibold">This session is being recorded.</p>
          <p className="mt-1 text-red-200/80">
            Every command you type and every response from the chassis is
            written to <code>command_transcripts</code> alongside an audit
            log entry. The transcript is downloadable via the button at
            top-right and viewable from the Terminal Sessions list page.
            Passwords entered at connect time are never recorded.
          </p>
          <button
            type="button"
            onClick={() => setShowRecInfo(false)}
            className="mt-2 rounded border border-red-700 px-2 py-0.5 text-red-100 hover:bg-red-950"
          >
            Dismiss
          </button>
        </div>
      )}
      <div
        ref={containerRef}
        className="flex-1 rounded-lg border border-slate-800 bg-slate-950 p-2"
      />
    </section>
  );
}
